import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ProgressIndicator,
  ProgressStep,
} from '@carbon/react';
import style from './wizard.module.scss';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { atom, Atom, useAtomValue, useSetAtom, WritableAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';

interface WizardProps {
  header: string | React.ReactNode;
  isOpen: boolean;
  onRequestClose: () => void;
  steps: {
    component: React.ReactNode;
    label: string;
    nextButtonLabel?: string;
    previousButtonLabel?: string;
    cancelButtonLabel?: string;
    exitButtonLabel?: string;
    errorLabel?: string;
    isNextEnabledAtom?: Atom<boolean> | Atom<Promise<boolean>>;
    isPreviousEnabledAtom?: Atom<boolean> | Atom<Promise<boolean>>;
    onNextAtom?: WritableAtom<
      void,
      unknown[],
      Promise<{ success: boolean; error?: string }>
    >;
    allowExit?: boolean;
    isInitialStep?: boolean;
  }[];
  navigateToStepAtom?: WritableAtom<number | null, [number | null], void>;
}

export const Wizard = ({
  header,
  steps,
  isOpen,
  onRequestClose,
  navigateToStepAtom,
}: WizardProps) => {
  const fallbackEnabledAtom = useMemo(() => {
    return atom(true);
  }, []);
  const fallbackNavigateToStepAtom = useMemo(() => {
    return atom<number | null>(null);
  }, []);

  const fallbackActionAtom = useMemo(() => {
    return atom(null, () => {
      return {
        success: true,
      };
    });
  }, []);

  const initialStepIndex = useMemo(() => {
    const index = steps.findIndex((step) => step.isInitialStep);
    return index !== -1 ? index : 0;
  }, [steps]);

  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const clearWizard = () => {
    batchedUpdates(() => {
      setIsError(false);
      setIsLoading(false);
    });
  };

  // Watch navigateToStepAtom if provided, use dummy atom otherwise
  const navigateToStepAtomToUse =
    navigateToStepAtom ?? fallbackNavigateToStepAtom;
  const navigateToStep = useAtomValue(navigateToStepAtomToUse);
  const setNavigateToStep = useSetAtom(navigateToStepAtomToUse);

  // Navigate to step when navigateToStepAtom changes
  useEffect(() => {
    if (
      navigateToStepAtom &&
      navigateToStep !== null &&
      navigateToStep >= 0 &&
      navigateToStep < steps.length
    ) {
      batchedUpdates(() => {
        clearWizard();
        setCurrentStepIndex(navigateToStep);
        // Reset the navigation atom after navigation
        setNavigateToStep(null);
      });
    }
  }, [navigateToStep, steps.length, setNavigateToStep, navigateToStepAtom]);

  const currentIsNextEnabledLoadable = useMemo(
    () =>
      loadable<boolean | Promise<boolean>>(
        steps[currentStepIndex].isNextEnabledAtom ?? fallbackEnabledAtom
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStepIndex]
  );

  const currentIsPreviousEnabledLoadable = useMemo(
    () =>
      loadable<boolean | Promise<boolean>>(
        steps[currentStepIndex].isPreviousEnabledAtom ?? fallbackEnabledAtom
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStepIndex]
  );

  const isNextEnabledLoadable = useAtomValue(currentIsNextEnabledLoadable);
  const isPreviousEnabledLoadable = useAtomValue(
    currentIsPreviousEnabledLoadable
  );

  const isNextEnabled =
    isNextEnabledLoadable.state === 'hasData'
      ? isNextEnabledLoadable.data
      : false;
  const isPreviousEnabled =
    isPreviousEnabledLoadable.state === 'hasData'
      ? isPreviousEnabledLoadable.data
      : false;

  const onNext = useSetAtom(
    steps[currentStepIndex].onNextAtom || fallbackActionAtom
  );

  const isLastStep = currentStepIndex === steps.length - 1;
  const isSubsequentStep = currentStepIndex > 0;

  const resetWizard = () => {
    batchedUpdates(() => {
      clearWizard();
      setCurrentStepIndex(initialStepIndex);
    });
  };

  const setWizardError = () => {
    batchedUpdates(() => {
      setIsError(true);
      setIsLoading(false);
    });
  };

  const handleNext = async (isExit?: boolean) => {
    setIsLoading(true);
    try {
      const result = await onNext();
      if (result.success) {
        batchedUpdates(() => {
          clearWizard();
          if (isExit) {
            handleClose();
          } else {
            setCurrentStepIndex(currentStepIndex + 1);
          }
        });
      } else {
        setWizardError();
      }
    } catch (error) {
      setWizardError();
    }
  };

  const handlePrevious = () => {
    batchedUpdates(() => {
      clearWizard();
      setCurrentStepIndex(currentStepIndex - 1);
    });
  };

  const handleClose = () => {
    batchedUpdates(() => {
      resetWizard();
      onRequestClose?.();
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <ComposedModal
        size="lg"
        className={style.modal}
        open={isOpen}
        preventCloseOnClickOutside
        onClose={handleClose}
      >
        <ModalHeader className={style.headerContainer}>
          {typeof header === 'string' ? (
            <p className={style.header}>{header}</p>
          ) : (
            header
          )}
        </ModalHeader>
        <ModalBody className={style.modalBody}>
          <div data-portal-container>
            <div className={style.progressContainer}>
              <ProgressIndicator vertical currentIndex={currentStepIndex}>
                {steps.map((step, index) => {
                  const isCurrentStep = currentStepIndex === index;
                  return (
                    <ProgressStep
                      key={step.label}
                      label={step.label}
                      invalid={isCurrentStep && isError}
                      secondaryLabel={
                        isCurrentStep && isError
                          ? steps[currentStepIndex].errorLabel
                          : ''
                      }
                    />
                  );
                })}
              </ProgressIndicator>
            </div>
            <div className={style.stepContent}>
              <Suspense fallback={<div>Loading...</div>}>
                {steps[currentStepIndex].component}
              </Suspense>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className={style.modalFooter}>
          <Button
            kind="ghost"
            onClick={handleClose}
            disabled={isLoading || isLastStep}
          >
            {steps[currentStepIndex].cancelButtonLabel || 'Cancel'}
          </Button>
          {isSubsequentStep ? (
            <Button
              className={style.backBtn}
              disabled={isLoading || !isPreviousEnabled}
              onClick={handlePrevious}
            >
              {steps[currentStepIndex].previousButtonLabel || 'Back'}
            </Button>
          ) : null}
          {steps[currentStepIndex].allowExit ? (
            <Button
              className={style.backBtn}
              onClick={() => handleNext(true)}
              disabled={isLoading || !isNextEnabled}
            >
              {steps[currentStepIndex].exitButtonLabel || 'Close'}
            </Button>
          ) : null}
          <Button
            disabled={isLoading || !isNextEnabled}
            kind="primary"
            onClick={() => {
              handleNext(isLastStep);
            }}
          >
            {steps[currentStepIndex].nextButtonLabel ||
              (isLastStep ? 'Close' : 'Next')}
          </Button>
        </ModalFooter>
      </ComposedModal>
    </>
  );
};
